import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { identityRolesApi } from '@/api/services/identityRoles';
import type { IdentityRoleDto } from '@/api/types/identity';
import { ApiError } from '@/api/client/abpError';
import { IdentityRolePermissions } from './permissions';
import { useDeleteRole } from './api';

const ROLES_PATH = '/admin/identity/roles';

export function IdentityRolesPage() {
  const can = usePermission();
  const canCreate = can(IdentityRolePermissions.create);
  const canUpdate = can(IdentityRolePermissions.update);
  const canDelete = can(IdentityRolePermissions.delete);
  const canManagePermissions = can(IdentityRolePermissions.managePermissions);

  const navigate = useNavigate();
  const toast = useToast();

  const [deleteTarget, setDeleteTarget] = useState<IdentityRoleDto | null>(null);

  const table = useServerDataTable<IdentityRoleDto>({
    queryKey: ['identityRoles', 'list'],
    fetcher: (req) => identityRolesApi.getListForDataTable(req),
    pageSize: 5,
  });
  const deleteMut = useDeleteRole();

  function requestDelete(role: IdentityRoleDto) {
    if (role.isStatic) {
      toast.error(`Role "${role.name}" is a static role and cannot be deleted.`);
      return;
    }
    setDeleteTarget(role);
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Deleted successfully');
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Failed to delete the role.');
        setDeleteTarget(null);
      },
    });
  }

  const columns: DataGridColumn<IdentityRoleDto>[] = [
    { key: 'name', header: 'Role Name', sortable: true, cell: (r) => r.name },
  ];

  // Edit opens the role page (name + permissions together).
  const canOpenEdit = canUpdate || canManagePermissions;
  const showRowActions = canOpenEdit || canDelete;

  return (
    <div>
      <PageHeader title="Roles" breadcrumb={['Identity management']} />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No roles found."
            toolbarEnd={
              canCreate && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => navigate(`${ROLES_PATH}/new`)}>
                  <i className="fa fa-plus me-1" />
                  Add Role
                </button>
              )
            }
            rowActions={
              showRowActions
                ? (row) => (
                    <div className="d-inline-flex gap-1 justify-content-center">
                      {canOpenEdit && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          title="Edit role & permissions"
                          onClick={() => navigate(`${ROLES_PATH}/${row.id}/edit`)}
                        >
                          <i className="fa fa-edit" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => requestDelete(row)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </div>
                  )
                : undefined
            }
          />
        </div>
      </div>

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Delete role"
        message={`Are you sure you want to delete the role "${deleteTarget?.name ?? ''}"?`}
        confirmLabel="Delete"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
